import { gql, useMutation } from "@apollo/client";
import { ArrowExitIcon } from "@visma-netvisor/nv-react-icons";
import {
  Layout,
  Link,
  Typography,
} from "@visma-netvisor/react-component-library";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { sendTag } from "../../analytics";
import { NETVISOR_LOGIN_URL, strings } from "../../constants";
import { cleanBrowserCache } from "../../utils/cookieUtil";
import { redirectToNetvisor } from "../../utils/redirectToNetvisor";
import { logFrontendError } from "../LogFrontendError";
import { useNetvisorLogin } from "../hooks/useNetvisorLogin";
import AccountingServiceNeedForm from "./AccountingServiceNeedForm";
import ActionButton from "./ActionButton";
import { useServiceNeedContext, QuestionSteps } from "../../context/ServiceNeedContext";
import {
  UpdateOpportunity,
  UpdateOpportunityVariables,
} from "./__generated__/UpdateOpportunity";

const UPDATE_OPPORTUNITY_MUTATION = gql`
  mutation UpdateOpportunity($id: String!, $accountingServiceNeed: Boolean!, $holviServiceNeed: Boolean!, $hasInsurance: Boolean!, $insuranceServiceNeed: Boolean!) {
    updateOpportunity(
      input: { 
        id: $id, 
        accountingServiceNeed: $accountingServiceNeed, 
        holviServiceNeed: $holviServiceNeed,
        hasInsurance: $hasInsurance,
        insuranceServiceNeed: $insuranceServiceNeed
      }
    ) {
      id
    }
  }
`;



const CompanyCreationSuccess = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const netvisorLoginMutation = useNetvisorLogin();
  const [updateOpportunity] = useMutation<
    UpdateOpportunity,
    UpdateOpportunityVariables
  >(UPDATE_OPPORTUNITY_MUTATION);
  
  const {
    setQuestionStep,
    accountingServiceNeed,
    holviServiceNeed,
    hasInsurance,
    insuranceServiceNeed,
  } = useServiceNeedContext();
  
  const leadId = sessionStorage.getItem("leadId");

  sendTag({ status: "companyCreationSuccess" });

  const updateAccountingServiceNeed = async (
    accountingServiceNeed: boolean,
    holviServiceNeed: boolean,
    hasInsurance: boolean,
    insuranceServiceNeed: boolean
  ) => {
    try {
      await updateOpportunity({
        variables: {
          id: leadId?.toString() || "",
          accountingServiceNeed: accountingServiceNeed,
          holviServiceNeed: holviServiceNeed,
          hasInsurance: hasInsurance,
          insuranceServiceNeed: insuranceServiceNeed
        },
      });
    } catch (error) {
      await logFrontendError({
        info: "Updating accountingServiceNeed for opportunity failed",
        details: { accountingServiceNeed },
        error,
      });
    }
  };

  const login = async () => {
    sendTag({ status: "redictedToNetvisor" });

    try {
      const token = await netvisorLoginMutation.mutateAsync();

      if (token) {
        sessionStorage.removeItem(strings.ORDER_ID_COOKIE_NAME);
        sessionStorage.removeItem(strings.ORDER_DATA_COOKIE_NAME);
        redirectToNetvisor(NETVISOR_LOGIN_URL, token);
      }
    } catch (error) {
      await logFrontendError({
        info: "Netvisor redirect failed",
        error,
      });
      setLoginError(true);
    }
  };

  if (loginError) {
    cleanBrowserCache();
  }

  const goToInitialQuestionStep = () => {
    setQuestionStep(QuestionSteps.INITIAL);
  };

  const goToFinalQuestionStep = () => {
    setQuestionStep(QuestionSteps.FINAL);
  }

  const handleUpdateAndLogin = async () => {
    setIsLoading(true);

    await updateAccountingServiceNeed(
      accountingServiceNeed ?? false, 
      holviServiceNeed ?? false, 
      hasInsurance ?? false, 
      insuranceServiceNeed ?? false
    );

    await login();
  };
 
  return (
    <>
      <Layout.Flex alignItems="center" flexDirection="column">
        <StyledTypography
          as="h1"
          color="secondary"
          variant="headingH1Semibold"
        >
          {t("OnlineStatus.CompanySuccessfullyCreated")}
        </StyledTypography>
        <AccountingServiceNeedForm />
        {loginError && (
          <StyledTypography
            as="h4"
            color="error"
            variant="contentHeadingH4Semibold"
            styleOverrides={{ paddingBottom: 130 }}
          >
            {t("OnlineStatus.ErrorTransitioningBankAccountSettings")}{" "}
            <Link href="https://login.netvisor.fi">
              <ArrowExitIcon />
            </Link>
          </StyledTypography>
        )}
        <ActionButton
          isLoading={isLoading}
          goToInitialQuestionStep={goToInitialQuestionStep}
          goToFinalQuestionStep={goToFinalQuestionStep}
          handleUpdateAndLogin={handleUpdateAndLogin}
        />
      </Layout.Flex>
    </>        
  );
};

export default CompanyCreationSuccess;

const StyledTypography = styled(Typography)`
  padding-top: 60px;
  @media (max-width: 768px) {
    font-size: 20px;
    padding-bottom: 16px;
  }
  @media (max-height: 480px) {
    padding-top: 16px;
  }
`;
